/**
 * The agent's opening line, built from this morning's brief.
 *
 * ─── BUILT IN CODE, NOT BY CLAUDE ───────────────────────────────────────────
 *
 * Nothing in this file calls the model. The opening line is the first thing on
 * the screen and the first thing the agent SAYS, so it has to be there before
 * any request could return — and a sentence a model writes about a brief is a
 * second account of figures the brief already carries. The API service's own
 * header says the same thing from the other end: every number is computed in
 * SQL, and if the agent phrases the brief, it phrases facts it was handed.
 *
 * ─── IT DISPLAYS; IT NEVER CALCULATES ───────────────────────────────────────
 *
 * Every figure below is read straight off `StoredBrief.facts`. `finishedJobCount`
 * is the API's DISTINCT over two event lists that can name the same job, and
 * `finishedWithoutTicket` is its own count — neither is re-derived here, and
 * the job list is never summed to produce one. Two answers to one question is
 * how nobody knows later which was right.
 *
 * The one place a number is composed rather than copied is the choice between
 * naming the jobs and counting them, and that is a branch rather than
 * arithmetic: at most three jobs are NAMED, and above that the API's own count
 * is spoken with no subtraction anywhere.
 *
 * ─── WHY IT CAN REFUSE ──────────────────────────────────────────────────────
 *
 * `null` means "no line" and the caller keeps its existing greeting. A brief
 * with no `facts` is not a quiet morning, and an opening line that narrated one
 * would be the silent-blank-field family spoken out loud.
 */
import { formatCalendarDate } from './calendar-date';
import { briefIsStale, distinctFinishedJobs, pendingAgentRows, ticketSummary } from './morning-brief';
import type { BriefJob, StoredBrief } from '../types/morning-brief';

/** Above this many finished jobs the line counts them instead of naming them. */
const MAX_NAMED_JOBS = 3;

/** Above this many pending agent types the line says how many kinds it left out. */
const MAX_NAMED_AGENT_TYPES = 3;

/**
 * The job numbers, in the brief's own order — or null if ANY of them is absent.
 *
 * All-or-nothing on purpose. A job with no number would have to be spoken as
 * something, and there is no honest something: an em dash read aloud is noise,
 * and dropping the job silently under-reports the morning. Refusing the whole
 * naming branch falls through to the count, which is a fact about all of them.
 */
function jobNumbers(jobs: BriefJob[]): string[] | null {
  const out: string[] = [];
  for (const j of jobs) {
    const n = typeof j.jobNumber === 'string' ? j.jobNumber.trim() : '';
    if (!n) return null;
    out.push(n);
  }
  return out.length > 0 ? out : null;
}

/** "A", "A and B", "A, B and C". */
function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * What finished in the period.
 *
 * The single-job arm carries that job's ticket position because it is the one
 * case where a specific answer fits in a clause — `ticketSummary` is the SAME
 * helper the Home card renders, so the card and the spoken line cannot describe
 * one job two ways.
 */
function finishedClause(facts: StoredBrief['facts']): string {
  const period = facts.period;
  const count = typeof period?.finishedJobCount === 'number' ? period.finishedJobCount : 0;
  if (count <= 0) return 'Nothing finished since the last brief.';

  const jobs = distinctFinishedJobs(period);
  const names = jobs.length <= MAX_NAMED_JOBS && jobs.length === count ? jobNumbers(jobs) : null;
  const noTicket = typeof period.finishedWithoutTicket === 'number' ? period.finishedWithoutTicket : 0;

  if (names && names.length === 1) {
    const job = jobs[0];
    return job.ticketTotal > 0
      ? `${names[0]} finished — ${ticketSummary(job)}.`
      : `${names[0]} finished, no ticket yet.`;
  }

  const head = names ? `${joinList(names)} finished` : `${count} jobs finished`;
  return noTicket > 0 ? `${head}, ${noTicket} with no ticket.` : `${head}.`;
}

/**
 * The offer. One, and it is the thing the facts actually support.
 *
 * "bill it out" is only offered when `standing.invoiceable` is above zero:
 * offering to do something the brief says there is nothing to do is how an
 * assistant teaches people to stop listening to it.
 */
function offerClause(facts: StoredBrief['facts'], finishedCount: number): string {
  const invoiceable = facts.standing?.invoiceable ?? 0;
  const pending = facts.pendingAgentRuns?.total ?? 0;

  if (finishedCount === 1) {
    return invoiceable > 0 ? 'Want to review it, or bill out what’s ready?' : 'Want to review it?';
  }
  if (finishedCount > 1) {
    return invoiceable > 0
      ? 'Want to run through them, or bill out what’s ready?'
      : 'Want to run through them?';
  }
  if (invoiceable > 0) return 'Want to bill out what’s ready?';
  if (pending > 0) return 'Want to go through what’s waiting?';
  return 'What do you want to start with?';
}

/**
 * "54 to review — 50 inspection reminders, 2 clock-out checks, 2 monthly cost
 * reviews." — or null, which is the only thing an empty queue may produce.
 *
 * ─── THE BREAKDOWN IS THE POINT, AND THE TOTAL IS STILL THE API'S ───────────
 *
 * A bare total was on screen and spoken for two PRs, and the operator reading
 * it could not tell what the runs WERE. `byAgent` already carries the answer
 * and the Home card already groups it, so the line reads the same rows through
 * the same helper — `pendingAgentRows` sorts largest-first and `agentRunLabel`
 * spells its own plurals. A second grouping written here would be two answers
 * to one question, and the one the operator hears would be the unreviewed one.
 *
 * The total is `pendingAgentRuns.total` verbatim, never the sum of the groups.
 * Those are the API's two figures and they are computed separately; adding the
 * rows up here would replace a fact with a derivation that can disagree with
 * the card sitting next to it.
 *
 * ─── WHY IT CAN SAY LESS THAN IT KNOWS ──────────────────────────────────────
 *
 * At most three types are named and the rest are counted as KINDS, not runs:
 * this line is spoken aloud, and Smith alone has three types where the estate
 * has twelve. "and 2 more kinds" cannot be misheard as 2 more runs, which a
 * bare "+2 more" beside three counted groups would be.
 *
 * `byAgent` absent while `total` is positive still speaks the total, with no
 * breakdown. There is nothing honest to say about groups nobody sent.
 *
 * ─── THE WORDING DIVERGES FROM THE HOME CARD, DELIBERATELY ──────────────────
 *
 * Home says "54 waiting on a person"; this says "54 to review". The operator
 * asked for the spoken head to name the ACTION — the card's phrase describes
 * the rows' state, which reads well beside a chevron and reads as a complaint
 * when a voice says it. One figure, two registers; the number and the groups
 * under it are identical, which is the part a reader could catch out.
 */
function pendingClause(pendingRuns: StoredBrief['facts']['pendingAgentRuns'] | undefined): string | null {
  const total = pendingRuns?.total ?? 0;
  if (total <= 0) return null;

  const rows = pendingAgentRows(pendingRuns?.byAgent ?? {});
  if (rows.length === 0) return `${total} to review.`;

  const named = rows.slice(0, MAX_NAMED_AGENT_TYPES).map((r) => r.label);
  const rest = rows.length - named.length;
  if (rest > 0) named.push(`${rest} more ${rest === 1 ? 'kind' : 'kinds'}`);

  return `${total} to review — ${named.join(', ')}.`;
}

/**
 * The whole line, or null when the brief cannot support one.
 *
 * `now` is injected so the staleness branch is assertable without faking a
 * clock — the same reason the API injects it into `generateBriefForCompany`.
 */
export function briefOpeningLine(
  brief: StoredBrief | null | undefined,
  now: Date = new Date(),
): string | null {
  const facts = brief?.facts;
  if (!brief || !facts || !facts.period || !facts.standing) return null;

  const parts: string[] = [];

  /*
   * A brief from an earlier tenant-local day says so FIRST, before any figure.
   * The endpoint returns the LATEST brief, not necessarily today's, and a line
   * that opened with two-day-old numbers would be narrating a different morning
   * as this one. `briefIsStale` is true only when it can PROVE the day is
   * earlier, so an unusable zone says nothing rather than guessing.
   *
   * The day is spoken absolutely — "Sep 10" — rather than as "yesterday": a
   * relative phrase is a second derivation from the same dates, and a missed
   * tick makes it wrong by more than one day.
   */
  if (briefIsStale(brief, now)) {
    const day = formatCalendarDate(brief.briefDate);
    parts.push(day ? `This is the brief from ${day}, not today.` : 'This brief is from an earlier day, not today.');
  }

  parts.push(finishedClause(facts));

  const invoiceable = facts.standing.invoiceable ?? 0;
  if (invoiceable > 0) parts.push(`${invoiceable} ready to bill.`);

  const pendingLine = pendingClause(facts.pendingAgentRuns);
  if (pendingLine) parts.push(pendingLine);

  parts.push(offerClause(facts, facts.period.finishedJobCount ?? 0));

  return parts.join(' ');
}
